// Shared option lists + compose/parse/format helpers for the structured assessor/collector pickers.
// Storage is unchanged — these only translate between the existing text columns and the pickers
// (e.g. month+day+N/A <-> "October 1" | "N/A"; decimal <-> "20%"). Reused by AddAssessorModal,
// AddCollectorModal, and the read-only detail views.
import { MONTHS, monthName } from './months';

export const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
// ⛔ DELIBERATELY NOT ON THE SHARED YEAR RANGE (src/constants/yearRange.js / folder_year_config).
// These are SCHEDULE ANCHORS, not value years: PICK_YEARS drives "Reappraisal Year" (AddAssessorModal)
// and the tax-bill-season year (AddCollectorModal). A reappraisal cycle legitimately points at a year
// beyond the value-year ceiling — a 2030 reappraisal is a real thing to record — and capping this to
// current_max_year would silently make it unenterable. Reviewed and left uncapped 2026-08-17. If the
// value-year range ever needs to govern these, that is a product decision, not a consistency cleanup.
export const PICK_YEARS = [2021, 2022, 2023, 2024, 2025, 2026, 2027, 2028];
export const REAPPRAISAL_CYCLES = ['1 Year', '2 Year', '3 Year', '4 Year', '5 Year', '6 Year', '7 Year', '8 Year'];
export const YESNO = ['Yes', 'No'];
export const FISCAL_YEARS = [
  'January 1 – December 31', 'February 1 – January 31', 'March 1 – February 28/29', 'April 1 – March 31',
  'May 1 – April 30', 'June 1 – May 31', 'July 1 – June 30', 'August 1 – July 31',
  'September 1 – August 31', 'October 1 – September 30', 'November 1 – October 31', 'December 1 – November 30',
];

// Installment classification (collector_installments.bill_type / .tax_type). Cook's inst-1 is the
// Non-Value-Based case — it bills a flat % of the prior year rather than an assessed value.
export const BILL_TYPES = ['Installment 1', 'Installment 2', 'Annual Bill'];
export const TAX_TYPES = ['Value Based', 'Non-Value Based'];

// ── Backfill seeding rule ────────────────────────────────────────────────────
// A stored value that EMBEDS a specific calendar year cannot carry to a different year's record --
// "October 1, 2026" / "July 2026" / "11/01/2026" are all wrong the moment you file them under 2025.
// A value with no year is stable policy and carries fine ("January 1", "July 1 - June 30", "1 Year").
//
// This is deliberately a runtime test on the VALUE, not a hardcoded field list: most rows came from the
// importer, which writes raw spreadsheet text, so any season/date column may or may not carry a year
// depending on how Nat's sheet phrased that particular row. Per-row, per-field.
//
// Apply ONLY to temporal fields (see SEEDABLE_* below). Never to names/addresses/URLs -- a street number
// like "2026 Main St" is not a calendar year.
export const embedsCalendarYear = (v) => v != null && /\b(19|20)\d{2}\b/.test(String(v));

// Temporal fields the year-test applies to when seeding a new year-version.
// reappraisal_year is the EXPLICIT EXCEPTION: it stores a bare year but describes the county's
// reappraisal SCHEDULE (a county reappraising in 2026 says 2026 on its 2025 record too), not a
// value-year-relative fact -- so it always carries and is not listed here.
export const SEEDABLE_ASSESSOR_TEMPORAL = [
  'assessment_date', 'reappraisal_cycle', 'fiscal_year',
  'compliance_season', 'extensions', 'notice_season', 'appeal_season',
];
export const SEEDABLE_COLLECTOR_TEMPORAL = ['tax_bill_season', 'fiscal_year'];

// ── percentage: stored decimal string  <->  "X%" input/display ──
// "0.2" -> "20%"; non-numeric (e.g. "Class 1: 30%…") passes through unchanged.
export const pctToInput = (stored) => {
  if (stored == null || String(stored).trim() === '') return '';
  const s = String(stored).trim();
  return /^\d*\.?\d+$/.test(s) ? `${parseFloat((parseFloat(s) * 100).toFixed(4))}%` : s;
};
// "20%" or "20" -> "0.2"; blank -> null; non-numeric stored as-is.
export const pctParse = (input) => {
  if (input == null) return null;
  const s = String(input).replace('%', '').trim();
  if (s === '') return null;
  return /^\d*\.?\d+$/.test(s) ? String(parseFloat((parseFloat(s) / 100).toFixed(6))) : String(input).trim();
};

// ── compliance: 3 modes — Month+Day ("October 1") | N/A | Other free-text ("3rd Monday in January") ──
// Priority on compose: N/A > Other free-text > Month/Day. `other` non-empty = Other mode.
export const composeCompliance = (month, day, na, other) => {
  if (na) return 'N/A';
  const o = other == null ? '' : String(other).trim();
  if (o) return o;
  if (month == null || month === '' || day == null || day === '') return null;
  return `${monthName(month)} ${day}`;
};
export const parseCompliance = (stored) => {
  const blank = { month: '', day: '', na: false, other: '' };
  if (stored == null) return blank;
  const s = String(stored).trim();
  if (s === '') return blank;
  if (/^n\/?a$/i.test(s)) return { month: '', day: '', na: true, other: '' };
  const m = s.match(/^([A-Za-z]+)\s+(\d{1,2})$/);
  if (m) { const mo = MONTHS.find(x => x.label.toLowerCase() === m[1].toLowerCase()); if (mo) return { month: mo.value, day: parseInt(m[2], 10), na: false, other: '' }; }
  return { month: '', day: '', na: false, other: s }; // relative/free-text (e.g. "3rd Monday in January") -> Other
};

// ── tax bill season, MONTH + YEAR only: {month, year}  <->  "October 2026" ──
// The stored collector value carries no day. parse tolerates a legacy "Month D, YYYY" by dropping the
// day, so an older "October 1, 2026" still round-trips (re-saving normalizes it to "October 2026").
export const composeSeasonMY = (month, year) => {
  if (month == null || month === '' || year == null || year === '') return null;
  return `${monthName(month)} ${year}`;
};
export const parseSeasonMY = (stored) => {
  const blank = { month: '', year: '' };
  if (stored == null) return blank;
  const m = String(stored).trim().match(/^([A-Za-z]+)\s+(?:\d{1,2},?\s*)?(\d{4})$/);
  if (m) { const mo = MONTHS.find(x => x.label.toLowerCase() === m[1].toLowerCase()); if (mo) return { month: mo.value, year: parseInt(m[2], 10) }; }
  return blank;
};

// ── installment due date: "2026-12-31" -> "December 31, 2026" (local, no TZ shift) ──
export const formatLongDate = (ymd) => {
  if (!ymd) return '—';
  const m = String(ymd).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${monthName(parseInt(m[2], 10))} ${parseInt(m[3], 10)}, ${m[1]}` : String(ymd);
};

// ═══ ASSESSOR TEMPLATE, 2026-10-09 (Philip) — Ratio dropdown, Month + Year seasons, year shifting ═══
// Storage stays TEXT and readable as before; these translate between the stored text and the pickers. A stored value
// the pickers cannot hold is NEVER converted or dropped: it comes back as `legacy` and is shown as "previously: …"
// until someone sets a value (decision 3 — no bulk conversion).

// Ratio dropdown: every 5% from 5–100, plus statutory ratios between those steps. Stored as a decimal string (0.45).
export const RATIO_OPTIONS = [5, 6, 10, 15, 16, 19, 20, 25, 30, 32, 33.33, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100];
// stored decimal → { sel: '45' | 'other' | '', other: '11.5%' | 'Class 1: 30%…' }
export const ratioIn = (stored) => {
  if (stored == null || String(stored).trim() === '') return { sel: '', other: '' };
  const s = String(stored).trim();
  if (/^\d*\.?\d+$/.test(s)) {
    const pct = Math.round(parseFloat(s) * 10000) / 100;
    if (RATIO_OPTIONS.includes(pct)) return { sel: String(pct), other: '' };
  }
  return { sel: 'other', other: pctToInput(s) };
};
export const ratioOut = (sel, other) => (sel === 'other' ? pctParse(other) : sel ? pctParse(sel) : null);

// The years a season picker offers: the record's value year, the year before and the two after — plus a stored year
// outside that window, so an existing value is always selectable rather than silently blanked.
export const seasonYears = (valueYear, stored) => {
  const vy = Number(valueYear) || new Date().getFullYear();
  const ys = [vy - 1, vy, vy + 1, vy + 2];
  const sy = Number(stored);
  return sy && !ys.includes(sy) ? [...ys, sy].sort((a, b) => a - b) : ys;
};

// Month + Year ("October 2026"). Reads "October 2026" and the older "October 1, 2026"; with `assumeYear`, also a bare
// "October 1" (Compliance Season's old Month + Day — decision 4: the record's value year). Anything else → legacy.
export const parseMonthYear = (stored, assumeYear = null) => {
  const blank = { month: '', year: '', legacy: '' };
  if (stored == null || String(stored).trim() === '') return blank;
  const s = String(stored).trim();
  const my = parseSeasonMY(s);
  if (my.month) return { month: my.month, year: my.year, legacy: '' };
  const md = s.match(/^([A-Za-z]+)\s+\d{1,2}$/);
  if (md && assumeYear) {
    const mo = MONTHS.find(x => x.label.toLowerCase() === md[1].toLowerCase());
    if (mo) return { month: mo.value, year: Number(assumeYear), legacy: '' };
  }
  return { ...blank, legacy: s };
};

// Compliance Season: Month + Year | N/A | Other (relative) — the only field keeping N/A and Other.
export const parseComplianceMY = (stored, valueYear) => {
  const blank = { month: '', year: '', na: false, other: '' };
  if (stored == null || String(stored).trim() === '') return blank;
  const s = String(stored).trim();
  if (/^n\/?a$/i.test(s)) return { ...blank, na: true };
  const p = parseMonthYear(s, valueYear);
  return p.month ? { ...blank, month: p.month, year: p.year } : { ...blank, other: s };   // relative text → Other
};
export const composeComplianceMY = (month, year, na, other) => {
  if (na) return 'N/A';
  const o = other == null ? '' : String(other).trim();
  if (o) return o;
  return composeSeasonMY(month, year);
};

// Assessment Date: Month + Day only now. "N/A" and Other text are no longer offered → legacy.
export const parseMonthDay = (stored) => {
  const p = parseCompliance(stored);
  return (p.na || p.other) ? { month: '', day: '', legacy: String(stored).trim() } : { month: p.month, day: p.day, legacy: '' };
};

// Year shifting (decision 2): copying a record to another year moves every calendar year in its seasons by the gap —
// "October 2026" → "October 2027" — instead of blanking it. The DB roll-forward does the same (shift_years_in_text).
export const shiftYears = (v, delta) => (v == null || !delta ? v
  : String(v).replace(/\b(19|20)\d{2}\b/g, (y) => String(Number(y) + delta)));
